import { useState } from "react";
import type { Block } from "../../api/types.ts";
import { type MediaItem, MediaPicker, UploadButton } from "../MediaLibrary.tsx";

/** Property form per block type. */
export function BlockFields({
  block,
  onChange,
}: {
  block: Block;
  onChange: (next: Block) => void;
}) {
  const set = (key: string, value: unknown) =>
    onChange({ ...block, props: { ...block.props, [key]: value } });

  const str = (key: string, fallback = "") => String(block.props[key] ?? fallback);
  const num = (key: string, fallback: number) => Number(block.props[key] ?? fallback);

  switch (block.type) {
    case "heading":
      return (
        <>
          <Field label="Text">
            <input value={str("text")} onChange={(e) => set("text", e.target.value)} />
          </Field>
          <div className="row">
            <Field label="Level">
              <select
                value={num("level", 2)}
                onChange={(e) => set("level", Number(e.target.value))}
              >
                <option value={1}>H1</option>
                <option value={2}>H2</option>
                <option value={3}>H3</option>
              </select>
            </Field>
            <AlignField value={str("align", "left")} onChange={(v) => set("align", v)} />
          </div>
        </>
      );

    case "text":
      return (
        <>
          <Field
            label="Content"
            hint="Basic HTML is allowed. Merge tags like {{first_name}} work here."
          >
            <textarea
              className="mono"
              rows={5}
              value={str("html")}
              onChange={(e) => set("html", e.target.value)}
            />
          </Field>
          <div className="row">
            <AlignField value={str("align", "left")} onChange={(v) => set("align", v)} />
            <Field label="Font size">
              <input
                type="number"
                value={num("fontSize", 15)}
                onChange={(e) => set("fontSize", Number(e.target.value))}
              />
            </Field>
          </div>
        </>
      );

    case "image":
      return (
        <>
          <Field label="Image">
            <input
              className="mono"
              value={str("src")}
              placeholder="https://"
              onChange={(e) => set("src", e.target.value)}
            />
            <ImageSource
              onChosen={(item, naturalWidth) =>
                onChange({
                  ...block,
                  props: {
                    ...block.props,
                    src: item.url,
                    // Never wider than the file itself: email clients upscale
                    // a small logo to the block width, and it shows.
                    ...(naturalWidth && naturalWidth < num("width", 552)
                      ? { width: naturalWidth }
                      : {}),
                  },
                })
              }
            />
          </Field>
          <Field
            label="Alt text"
            hint="Many clients block images by default, so this is often all the recipient sees."
          >
            <input value={str("alt")} onChange={(e) => set("alt", e.target.value)} />
          </Field>
          <div className="row">
            <Field label="Links to">
              <input
                className="mono"
                value={str("href")}
                onChange={(e) => set("href", e.target.value)}
              />
            </Field>
            <Field label="Width (px)">
              <input
                type="number"
                value={num("width", 552)}
                onChange={(e) => set("width", Number(e.target.value))}
              />
            </Field>
          </div>
        </>
      );

    case "button":
      return (
        <>
          <div className="row">
            <Field label="Label">
              <input value={str("label")} onChange={(e) => set("label", e.target.value)} />
            </Field>
            <Field label="Links to">
              <input
                className="mono"
                value={str("href")}
                onChange={(e) => set("href", e.target.value)}
              />
            </Field>
          </div>
          <div className="row">
            <Field label="Background">
              <input
                type="color"
                value={str("bg", "#7da51a")}
                onChange={(e) => set("bg", e.target.value)}
              />
            </Field>
            <Field label="Text colour">
              <input
                type="color"
                value={str("fg", "#ffffff")}
                onChange={(e) => set("fg", e.target.value)}
              />
            </Field>
          </div>
        </>
      );

    case "divider":
      return (
        <Field label="Colour">
          <input
            type="color"
            value={str("color", "#e5e5e5")}
            onChange={(e) => set("color", e.target.value)}
          />
        </Field>
      );

    case "spacer":
      return (
        <Field label="Height (px)">
          <input
            type="number"
            value={num("height", 24)}
            onChange={(e) => set("height", Number(e.target.value))}
          />
        </Field>
      );

    case "footer":
      return (
        <>
          <Field
            label="Postal address"
            hint="A physical address is required on commercial mail in most jurisdictions."
          >
            <input
              value={str("companyAddress")}
              onChange={(e) => set("companyAddress", e.target.value)}
            />
          </Field>
          <Field label="Unsubscribe link">
            <label className="checkline">
              <input
                type="checkbox"
                checked={block.props.showUnsubscribe !== false}
                onChange={(e) => set("showUnsubscribe", e.target.checked)}
              />
              Include an unsubscribe link
            </label>
            <div className="hint">
              Removing this does not remove the List-Unsubscribe header, which is always sent — but
              a visible link is what most recipients look for.
            </div>
          </Field>
        </>
      );

    default:
      return null;
  }
}

/** Upload a new image, or pick one already in the media library. */
function ImageSource({
  onChosen,
}: {
  onChosen: (item: MediaItem, naturalWidth: number | null) => void;
}) {
  const [picking, setPicking] = useState(false);

  const choose = (item: MediaItem) => {
    const img = new Image();
    img.onload = () => onChosen(item, img.naturalWidth || null);
    img.onerror = () => onChosen(item, null);
    img.src = item.url;
  };

  return (
    <div className="image-source">
      <UploadButton label="Upload…" onUploaded={choose} />
      <button type="button" className="btn ghost small" onClick={() => setPicking(true)}>
        Choose from media
      </button>
      {picking && <MediaPicker onPick={choose} onClose={() => setPicking(false)} />}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="field">
      {/* biome-ignore lint/a11y/noLabelWithoutControl: the control is the child passed in. */}
      <label>{label}</label>
      {children}
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

function AlignField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field label="Align">
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="left">Left</option>
        <option value="center">Center</option>
        <option value="right">Right</option>
      </select>
    </Field>
  );
}
